import type { CSSProperties, FC } from "react";

import classNames from "classnames";

import { i18nFilter } from "app/filters/i18nFilter";

import { WebsiteTooltip } from "../../Tooltip/WebsiteTooltip/WebsiteTooltip";
import type { ITableCellProps } from "../interfaces/ITableCellProps";
import { trackEvent } from "../SWReactTableTrackEvent";

import { QualificationSideBarCellButton } from "./QualificationSideBarCellButton";
import { UpgradeLink } from "./UpgradeLink";

interface IWebsiteTooltipTopCellProps extends Partial<ITableCellProps> {
    hideTrackButton?: boolean;
    secondaryIcon?: string;
    secondaryUrl?: string;
    secondaryValue?: string;
    handleOpenQualificationSideBar?: (row: any) => void;
    onCellClick?: (e: FixmeType, index: number, isEnrichedRowTriggerClick?: boolean) => void;
    hideDomainCount?: boolean;
}

export const WebsiteTooltipTopCell: FC<IWebsiteTooltipTopCellProps> = ({
    row,
    value,
    toggleChildRows,
    showMoreChildRows,
    visibleParents,
    tableMetadata,
    metadata,
    tableOptions,
    hideTrackButton,
    secondaryIcon,
    secondaryUrl,
    secondaryValue,
    handleOpenQualificationSideBar,
    onCellClick,
    hideDomainCount,
}) => {
    const children = row.Children || row.children || [],
        isChild = row.parent,
        isExpanded =
            visibleParents &&
            (visibleParents as FixmeType)[row.index] &&
            (visibleParents as FixmeType)[row.index].expanded,
        showMore = isChild && row.isLast && row.index + 1 !== row.parent.childsCount,
        showMoreStyle: CSSProperties = showMore ? { position: "relative", top: "-6px" } : {},
        classes = classNames("swTable-rowToggle", isExpanded ? "sw-icon-collapse" : "sw-icon-expand"),
        favicon = row.Icon || row.Favicon || row.favicon;

    let wrapperClass = (tableMetadata || metadata || {})["hasChilds"] ? "domain-cell" : "cell-padding";
    wrapperClass += " escape-max-width";
    wrapperClass += handleOpenQualificationSideBar ? " sidebar-button" : "";

    if (value !== "grid.upgrade") {
        if (secondaryIcon && secondaryUrl && secondaryValue) {
            return (
                <div
                    className={wrapperClass}
                    style={showMoreStyle}
                >
                    <span
                        className="favicon swTable-websiteTooltipCell-favicon"
                        data-automation="domain-favicon-container"
                    >
                        <img
                            data-automation="domain-favicon"
                            alt="website icon"
                            src={secondaryIcon}
                            className="favicon swTable-websiteTooltipCell-favicon"
                        />
                    </span>
                    <WebsiteTooltip
                        domain={secondaryValue}
                        hideTrackButton={hideTrackButton}
                    >
                        <div
                            className={"swTable-content-large text"}
                            onClick={() => {
                                trackEvent(tableOptions || metadata, "Internal Link", secondaryValue, "click");
                            }}
                        >
                            <a
                                href={secondaryUrl}
                                target="_self"
                                onClick={row.onNavigate}
                                className="cell-clickable"
                                data-automation="domain-name"
                            >
                                {secondaryValue}
                            </a>
                        </div>
                    </WebsiteTooltip>
                    <a
                        className="swTable-linkOut sw-icon-bounce-rate"
                        href={`http://${value}`}
                        onClick={() => {
                            trackEvent(tableOptions, "External Link", secondaryValue, "click");
                        }}
                        target="_blank"
                    />
                </div>
            );
        } else {
            return (
                <>
                    <div
                        className={wrapperClass}
                        style={showMoreStyle}
                    >
                        {children.length ? (
                            <div>
                                <i
                                    className={classes}
                                    onClick={() => toggleChildRows?.(row, value)}
                                />
                            </div>
                        ) : null}
                        <span
                            className="favicon swTable-websiteTooltipCell-favicon"
                            data-automation="domain-favicon-container"
                        >
                            <img
                                data-automation="domain-favicon"
                                alt="website icon"
                                src={favicon}
                                className="favicon swTable-websiteTooltipCell-favicon"
                            />
                        </span>
                        <WebsiteTooltip
                            domain={value}
                            mainDomainOnly={false}
                            hideTrackButton={hideTrackButton}
                            handleOpenQualificationSideBar={
                                handleOpenQualificationSideBar
                                    ? (e) => {
                                          if (typeof onCellClick === "function") {
                                              onCellClick(e, -1);
                                          }
                                          handleOpenQualificationSideBar({
                                              Domain: value,
                                              Favicon: favicon,
                                          });
                                      }
                                    : undefined
                            }
                        >
                            <div
                                className={
                                    children.length ? "swTable-content-small text" : "swTable-content-large text"
                                }
                                onClick={() => {
                                    trackEvent(tableOptions || metadata, "Internal Link", value, "click");
                                }}
                            >
                                {row.url ? (
                                    <a
                                        href={row.url}
                                        target="_self"
                                        onClick={row.onNavigate}
                                        className="cell-clickable"
                                        data-automation="domain-name"
                                    >
                                        {value}
                                    </a>
                                ) : (
                                    <span data-automation="domain-name">{value}</span>
                                )}
                            </div>
                        </WebsiteTooltip>
                        {hideDomainCount ? null : children.length ? (
                            <span className="u-vAlignMiddle">({children.length})</span>
                        ) : null}

                        <a
                            className="swTable-linkOut sw-icon-bounce-rate"
                            href={`http://${value}`}
                            onClick={() => {
                                trackEvent(tableOptions, "External Link", value, "click");
                            }}
                            target="_blank"
                        />
                        {showMore ? (
                            <button
                                className="swTable-showMore showMore-reset showMore-translate"
                                onClick={(e) => showMoreChildRows?.(row, value)}
                            >
                                {i18nFilter()("grid.showmore")}
                            </button>
                        ) : null}
                    </div>
                    {handleOpenQualificationSideBar ? (
                        <QualificationSideBarCellButton
                            domain={value}
                            favicon={favicon}
                            handleOpenQualificationSideBar={handleOpenQualificationSideBar}
                        />
                    ) : null}
                </>
            );
        }
    } else {
        return <UpgradeLink hookType="website" />;
    }
};
WebsiteTooltipTopCell.displayName = "WebsiteTooltipTopCell";
